import { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Stack, Typography, Tooltip, Switch, TextField, SelectChangeEvent } from '@mui/material';

import { AddCircle, Edit, SearchNormal1 } from 'iconsax-react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import CustomTable from 'components/@extended/CustomTable';
import SkeletonTable from 'components/SkeletonTable';
import IconButton from 'components/@extended/IconButton';
import { getPlaceholder } from 'utils/getPlaceHolder';
import Snackbar from 'utils/Snackbar';

import { dispatch, useSelector } from 'store';
import useConfig from 'hooks/useConfig';
import useContemp from 'hooks/useContemp';
import useAuth from 'hooks/useAuth';

import { getSubTrainingCenter } from 'services/organization.service';
import { organizationUpdateStatus } from 'services/superAdminDashboard.service';
import {
  setorganizationsdataPageIndex,
  setorganizationsdatastatus,
  setorganizationsdataSearch,
  setorganizationsdataTableSize,
  updateorganizationsdata,
  setOrganizationTotalCount,
  resetStateorganizationsdata,
  setorganizationsdataFilterType,
  setOrganizationSorting
} from 'store/reducers/organizationsdata';

const TrainingCenterList = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { i18n } = useConfig();
  const { setData, prevStoredData } = useContemp();
  const { organization, user } = useAuth();

  const pageIndex = useSelector((state: any) => state.organizationsdata.pageIndex);
  const size = useSelector((state: any) => state.organizationsdata.size);
  const search = useSelector((state: any) => state.organizationsdata.search);
  const count = useSelector((state: any) => state.organizationsdata.count);
  const selectedFilter = useSelector((state: any) => state.organizationsdata.filter);
  const dataSet = useSelector((state: any) => state.organizationsdata.organizationsdata?.data ?? []);
  const filterDataset = dataSet.filter((item: any) => item.isParent !== true);
  const [sortArray, setSortArray] = useState<any[]>([]);
  const [value, setValue] = useState('');
  const [isLoading, setLoading] = useState(true);

  const sort: Record<string, 1 | -1> = {};
  sortArray.forEach((s) => {
    if (s.sort) sort[s.id] = s.desc ? -1 : 1;
  });

  const fetchTrainingCenters = async () => {
    setLoading(true);

    const reqData: any = {
      parentOrgId: organization?._id,
      limit: size,
      skip: pageIndex,
      sort
    };

    if (organization?.parentOrganizationId) {
      reqData.trainingPartnerId = user?._id;
    }

    if (search) reqData.search = search;
    if (selectedFilter) reqData.type = selectedFilter;

    try {
      const result = await getSubTrainingCenter(reqData);

      dispatch(updateorganizationsdata(result?.data?.data ?? []));
      dispatch(setOrganizationTotalCount(result?.data?.count ?? 0));
    } catch (err) {
      console.error(err);
      Snackbar('Failed to load training centers', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainingCenters();
  }, [pageIndex, size, search, selectedFilter, JSON.stringify(sortArray)]);

  useEffect(() => {
    if (prevStoredData?.[pathname]) {
      const prev = prevStoredData[pathname];
      dispatch(setorganizationsdataPageIndex(prev.pageIndex ?? 0));
      dispatch(setorganizationsdataTableSize(prev.pageSize ?? 10));
      dispatch(setorganizationsdatastatus(!!prev.status));
      dispatch(setorganizationsdataFilterType(prev.selectedFilter ?? ''));
      dispatch(setorganizationsdataSearch(prev.search ?? ''));
    }

    return () => {
      dispatch(resetStateorganizationsdata());
    };
  }, [pathname]);

  const handleSortChange = useCallback(
    (column: any) => {
      const id = column.id || column.name;
      const index = sortArray.findIndex((item) => item.id === id);
      const next = [...sortArray];

      if (index === -1) next.push({ id, sort: true, desc: false });
      else if (!next[index].desc) next[index].desc = true;
      else next.splice(index, 1);

      setSortArray(next);
      dispatch(setOrganizationSorting(next));
    },
    [sortArray]
  );

  const columns = useMemo(
    () => [
      { Header: 'Name', accessor: 'name' },
      { Header: 'Type', accessor: 'type' },
      {
        Header: 'Admin User',
        accessor: (row: any) =>
          row?.firstAdminUser ? (
            <Tooltip title={row.firstAdminUser.email}>
              <span>
                {row.firstAdminUser.firstName} {row.firstAdminUser.lastName}
              </span>
            </Tooltip>
          ) : (
            '-'
          )
      },
      {
        Header: 'Created At',
        accessor: 'created_at',
        Cell: ({ value }: any) => (value ? new Date(value).toLocaleString() : '-')
      },
      {
        Header: 'Status',
        Cell: ({ row }: any) => {
          const handleToggle = async () => {
            const newDeleted = !row.original.deleted;

            try {
              const res = await organizationUpdateStatus({
                organizationId: row.original._id,
                deleted: newDeleted
              });

              if (res?.status === 200) {
                Snackbar('Status updated', 'success');
                dispatch(
                  updateorganizationsdata({
                    data: dataSet.map((item: any) => (item._id === row.original._id ? { ...item, deleted: newDeleted } : item))
                  })
                );
              } else {
                Snackbar('Update failed', 'error');
              }
            } catch {
              Snackbar('Something went wrong', 'error');
            }
          };

          return <Switch checked={!row.original.deleted} onChange={handleToggle} />;
        }
      },
      {
        Header: 'Options',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }: any) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={<FormattedMessage id="Edit" />}>
              <span>
                <IconButton
                  color="primary"
                  onClick={() => {
                    setData({ [pathname]: { size, pageIndex } });
                    navigate('/trainingcenter/edit', {
                      state: {
                        isEdit: true,
                        data: row.original
                      }
                    });
                  }}
                >
                  <Edit />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )
      }
    ],
    [dataSet, navigate, pageIndex, pathname, setData, size]
  );

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <MainCard
        title={
          <Stack direction="row" alignItems="center" spacing={2}>
            <BackToggle show />
            <Box>
              <Typography variant="h5">
                <FormattedMessage id="Training Centers" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage training centers with options to add, edit, or change status.
              </Typography>
            </Box>
          </Stack>
        }
        content={false}
        secondary={
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField value={value} onChange={(e) => setValue(e.target.value)} placeholder={getPlaceholder(i18n, count)} />
            <IconButton variant="outlined" onClick={() => dispatch(setorganizationsdataSearch(value))} disabled={!value}>
              <SearchNormal1 />
            </IconButton>
            <IconButton variant="dashed" onClick={() => navigate('/trainingcenter/add')}>
              <AddCircle />
            </IconButton>
          </Stack>
        }
      />

      <MainCard content={false}>
        {isLoading ? (
          <SkeletonTable columns={columns} />
        ) : (
          <CustomTable
            columns={columns}
            data={filterDataset}
            count={count}
            size={size}
            pageIndex={pageIndex}
            sort={sortArray}
            setSortingData={handleSortChange}
            handlePageChange={(_: any, page: number) => dispatch(setorganizationsdataPageIndex(page))}
            handleSizeChange={(e: SelectChangeEvent<number>) => dispatch(setorganizationsdataTableSize(Number(e.target.value)))}
          />
        )}
      </MainCard>
    </Box>
  );
};

export default TrainingCenterList;
